/*
 * Do not remove or alter the notices in this preamble.
 * This software code regards iO Custom Software.
 * Copyright © 2026 iO The Netherlands B.V. and/or its affiliates.
 * www.iodigital.com All rights reserved. License grant and user rights and obligations
 * according to applicable license agreement. Please contact legal@iodigital.com for
 * questions regarding license and user rights.
 */

import { css, html, LitElement } from 'lit';
import { customElement } from 'lit/decorators.js';

import {
  BREAKPOINT_EXTRA_LARGE_PX,
  BREAKPOINT_LARGE_PX,
  BREAKPOINT_MEDIUM_PX,
} from '@/settings';
import { baseStyles, skeletonStyles } from '@/styles';

@customElement('fg-pricebreakdown-skeleton-loader')
export class FgPricebreakdownSkeletonLoader extends LitElement {
  render() {
    return html`
      <!-- Price Breakdown Section -->
      <div class="skeleton-section">
        <!-- Mobile & Tablet & Desktop Price Breakdown Section -->
        <div class="price-breakdown-mobile">
          <div class="skeleton-price-box"></div>

          <div class="skeleton-row-mobile price-line">
            <div class="skeleton-item mobile-only"></div>
            <div class="skeleton-item"></div>
          </div>
          <div class="skeleton-row-mobile price-line">
            <div class="skeleton-item mobile-only"></div>
            <div class="skeleton-item"></div>
          </div>
          <div
            class="skeleton-row-mobile skeleton-row--tablet-and-mobile-only skeleton-row-content-end-only-mobile"
          >
            <div class="skeleton-item skeleton-item--end"></div>
          </div>
        </div>
        <!-- Large Desktop Price Breakdown Section -->
        <div class="price-breakdown-desktop">
          <div class="skeleton-price-box"></div>
          <div class="skeleton-desktop-items">
            <div class="skeleton-item"></div>
            <div class="skeleton-item skeleton-item--spaced"></div>
          </div>
        </div>
        <div
          class="skeleton-item skeleton-item--end skeleton-item--desktop-only"
        ></div>
        <div class="skeleton-button"></div>
        <div class="skeleton-item skeleton-item--center"></div>
      </div>

      <!-- USPS and Action Buttons Section -->
      <div class="skeleton-section skeleton-section--usps">
        <div class="usps-container">
          <div class="buttons-container">
            <div class="skeleton-button skeleton-button--sized"></div>
            <div class="skeleton-button skeleton-button--sized"></div>
          </div>
          <div class="usps-items skeleton-section-only-tablet-and-desktop">
            <div class="usps-item">
              <div class="usps-icon"></div>
              <div class="skeleton-item skeleton-item"></div>
            </div>
            <div class="usps-item">
              <div class="usps-icon"></div>
              <div class="skeleton-item skeleton-item"></div>
            </div>
            <div class="usps-item">
              <div class="usps-icon"></div>
              <div class="skeleton-item skeleton-item"></div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  static styles = [
    baseStyles,
    skeletonStyles,
    css`
      .skeleton-section--usps {
        background-color: transparent;
      }

      /* Price Breakdown Mobile & Tablet */
      .price-breakdown-mobile {
        display: flex;
        flex-direction: column;
      }

      .skeleton-price-box {
        background: var(--skeleton-gray);
        border-radius: var(--skeleton-border-radius);
        height: 5rem;
        width: 12rem;
        align-self: flex-end;
        margin-bottom: 10px;
      }

      .skeleton-row-mobile {
        display: flex;
        justify-content: space-between;
        margin-bottom: 0.75rem;
      }

      .skeleton-row--tablet-and-mobile-only,
      price-line {
        display: flex;
      }

      .skeleton-row-mobile--hidden-mobile {
        display: none;
      }

      .skeleton-item {
        width: 7rem;
      }

      .skeleton-item--end {
        display: flex;
        justify-self: flex-end;
        margin-bottom: 1.25rem;
      }

      .skeleton-item--center {
        display: flex;
        justify-self: center;
        width: 10rem;
        margin-top: 1.5rem;
      }

      .skeleton-button {
        border-radius: 60px;
      }

      /* Desktop Price Breakdown */
      .price-breakdown-desktop {
        display: none;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 5px;
      }

      .skeleton-desktop-items {
        display: flex;
        flex-direction: column;
        align-items: center;
      }

      .skeleton-item--spaced {
        margin-top: 0.75rem;
      }

      .skeleton-item--desktop-only {
        display: none;
        margin-bottom: 1.25rem;
      }

      /* USPS Section */
      .usps-container {
        display: flex;
        flex-direction: column-reverse;
        gap: 1.5rem;
      }

      .buttons-container {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        flex: 1;
      }

      .skeleton-button--sized {
        width: 100%;
      }

      .usps-items {
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 15px;
      }

      .usps-item {
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }

      .usps-icon {
        background: var(--skeleton-gray);
        border-radius: 50%;
        height: 1.25rem;
        width: 1.25rem;
      }

      .skeleton-section-only-tablet-and-desktop {
        display: none;
      }

      @media screen and (max-width: ${BREAKPOINT_LARGE_PX}px) {
        .skeleton-row-content-end-only-mobile {
          justify-content: flex-end;
        }
      }

      @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
        .mobile-only {
          display: none;
        }

        .price-line {
          display: flex;
          justify-content: flex-end;
        }

        .price-line:first-child {
          display: none;
        }

        .skeleton-section {
          padding: 1.25rem;
        }

        .skeleton-section-only-tablet-and-desktop {
          display: flex;
        }

        .skeleton-row-mobile--hidden-mobile {
          display: flex;
        }

        .skeleton-item {
          width: 6rem;
        }

        .skeleton-desktop-items {
          align-self: center;
        }

        .skeleton-button--sized {
          width: 250px;
        }

        .usps-container {
          flex-direction: row;
          column-gap: 15px;
        }

        .buttons-container {
          flex: initial;
        }
      }

      @media screen and (min-width: ${BREAKPOINT_LARGE_PX}px) {
        .skeleton-row--tablet-and-mobile-only,
        price-line {
          display: none;
        }

        .skeleton-row-mobile--hidden-mobile {
          display: none;
        }

        .skeleton-item--desktop-only {
          display: block;
        }

        .skeleton-button--sized {
          width: 360px;
        }
      }

      @media screen and (min-width: ${BREAKPOINT_EXTRA_LARGE_PX}px) {
        .skeleton-price-box {
          margin-bottom: 0px;
        }
        .price-breakdown-mobile {
          display: none;
        }
        .price-breakdown-desktop {
          display: flex;
          flex-direction: row;
        }
      }
    `,
  ];
}
